import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useData } from "@/contexts/DataContext";
import { Logo } from "@/components/Logo";
import { Mail, CheckCircle, Loader2, ImagePlus, X, ChevronLeft } from "lucide-react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { functionsAsia } from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";

// 🚨 NEW: Type definition for our compressed images
type ProcessedImage = {
  filename: string;
  contentType: string;
  content: string; // Base64 data
  preview: string; // Data URL for the UI preview
};

export default function Support() {
  const { user } = useAuth();
  const { businessProfile, settingsLoaded, isUltimateOwnerMobile } = useData();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("General");
  const [message, setMessage] = useState("");

  // 🚨 NEW: State for processed images and loading UI
  const [images, setImages] = useState<ProcessedImage[]>([]);
  const [isProcessingImages, setIsProcessingImages] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (settingsLoaded) {
      if (!name) {
        const prefillName = user?.displayName || businessProfile?.ownerName || "";
        if (prefillName) setName(prefillName);
      }
      if (!email) {
        const prefillEmail = businessProfile?.businessEmail || user?.email || "";
        if (prefillEmail) setEmail(prefillEmail);
      }
    }
  }, [settingsLoaded, businessProfile, user, name, email]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const newFiles = Array.from(e.target.files);

    if (images.length + newFiles.length > 4) {
      toast({
        title: "Limit reached",
        description: "You can only upload up to 4 files.",
        variant: "destructive",
      });
      return;
    }

    // 🚨 Turn on the loading spinner
    setIsProcessingImages(true);

    try {
      // Process and compress all selected images simultaneously
      const processedImages = await Promise.all(
        newFiles.map(async (file) => {
          return new Promise<ProcessedImage>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
              const img = new Image();
              img.onload = () => {
                const canvas = document.createElement("canvas");
                // Max dimension of 1200px to keep text readable but drop file size drastically
                const MAX_DIM = 1200;
                let w = img.width;
                let h = img.height;

                if (w > h && w > MAX_DIM) {
                  h *= MAX_DIM / w;
                  w = MAX_DIM;
                } else if (h > MAX_DIM) {
                  w *= MAX_DIM / h;
                  h = MAX_DIM;
                }

                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                if (ctx) {
                  // Fill white background in case of transparent PNGs
                  ctx.fillStyle = "white";
                  ctx.fillRect(0, 0, w, h);
                  ctx.drawImage(img, 0, 0, w, h);
                }

                // Compress to 60% quality JPEG
                const preview = canvas.toDataURL("image/jpeg", 0.6);
                const content = preview.split(",")[1];

                resolve({
                  filename: file.name.replace(/\.[^/.]+$/, "") + ".jpg",
                  contentType: "image/jpeg",
                  content,
                  preview
                });
              };
              img.onerror = reject;
              img.src = event.target?.result as string;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        })
      );

      setImages((prev) => [...prev, ...processedImages].slice(0, 4));
    } catch (error) {
      toast({
        title: "Processing Failed",
        description: "Could not process one or more images.",
        variant: "destructive",
      });
    } finally {
      // 🚨 Turn off the loading spinner and reset input
      setIsProcessingImages(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim().length < 10) {
      toast({
        title: "Message too short",
        description: "Please describe your issue in at least 10 characters.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const sendSupportEmail = httpsCallable(functionsAsia, 'triggerSupportEmail');
      await sendSupportEmail({
        name: name.trim(),
        email: email.trim(),
        topic,
        message: message.trim(),
        // Just map out the processed Base64 data to send to the backend
        attachments: images.map(img => ({
          filename: img.filename,
          contentType: img.contentType,
          content: img.content
        })),
      });

      setSubmitted(true);
    } catch (error: any) {
      toast({
        title: "Failed to send",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]">
      {isUltimateOwnerMobile ? (
        <div className="px-4 pb-2" style={{ paddingTop: "calc(env(safe-area-inset-top) + 1.25rem)" }}>
          <div className="flex items-start gap-3">
            <button onClick={() => setLocation("/settings")} className="rounded-full p-2 border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 mt-1 shrink-0">
              <ChevronLeft className="h-4 w-4 text-[#0F172A] dark:text-[#EEEAE3]" />
            </button>
            <div className="min-w-0">
              <h1 className="text-2xl font-black uppercase tracking-tighter leading-[0.95] text-[#0F172A] dark:text-[#EEEAE3]">
                NEED <span className="text-[#8B1A1A]">HELP?</span>
              </h1>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-[#8A8A92] mt-2">
                We respond within 24 hours
              </p>
            </div>
          </div>
        </div>
      ) : (
        <header className="sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <Logo size="md" />
            </div>
          </div>
        </header>
      )}

      <div className="flex-1 flex flex-col justify-center px-4 py-6 pb-24 overflow-y-auto">
        {!isUltimateOwnerMobile && (
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Mail className="h-6 w-6 text-[#8B2121] dark:text-red-400" />
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">Need Help?</h1>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              We usually respond within 24 hours
            </p>
          </div>
        )}

        {submitted ? (
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 shadow-sm border border-slate-100 dark:border-slate-800 text-center">
            <div className="w-16 h-16 bg-green-50 dark:bg-green-950/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Message Sent!</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              We've received your message and will get back to you at <strong>{email}</strong> within 24 hours.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-5 shadow-sm border border-slate-100 dark:border-slate-800 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
                  Your Name
                </label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Enter your name" className="w-full h-11 px-4 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#8B2121]/20 focus:border-[#8B2121]/40" />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
                  Email Address
                </label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Enter your email" className="w-full h-11 px-4 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#8B2121]/20 focus:border-[#8B2121]/40" />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
                  Topic
                </label>
                <select
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className="w-full h-11 px-4 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#8B2121]/20 focus:border-[#8B2121]/40"
                >
                  {["General", "Technical", "Billing", "Sales", "Feature Request"].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
                  Your Message
                </label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} required placeholder="Describe your issue or question..." rows={5} className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#8B2121]/20 focus:border-[#8B2121]/40 resize-none" />
              </div>

              <div>
                <label className="flex items-center justify-between text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                  <span>Uploads</span>
                  <span className="text-[10px] bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">{images.length} / 4</span>
                </label>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/png, image/jpeg, image/jpg"
                  multiple
                  className="hidden"
                />

                <div className="flex flex-wrap gap-3">
                  {images.map((img, idx) => (
                    <div key={idx} className="relative w-16 h-16 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 overflow-hidden group">
                      <img src={img.preview} alt="preview" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 shadow-md scale-90 active:scale-75 transition-transform"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}

                  {/* 🚨 Loading State built directly into the Add button */}
                  {images.length < 4 && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isProcessingImages}
                      className="w-16 h-16 flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors active:scale-95 disabled:opacity-70 disabled:active:scale-100"
                    >
                      {isProcessingImages ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin text-[#8B2121] dark:text-red-400" />
                          <span className="text-[9px] font-bold text-[#8B2121] dark:text-red-400">Processing</span>
                        </>
                      ) : (
                        <>
                          <ImagePlus className="w-5 h-5" />
                          <span className="text-[9px] font-bold">Add</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <button type="submit" disabled={isSubmitting || isProcessingImages} className="w-full py-4 rounded-3xl font-bold text-white text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-transform shadow-sm disabled:opacity-60" style={{ background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)" }}>
              {isSubmitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>
              ) : (
                <><Mail className="h-4 w-4" /> Send Message</>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}